import {
  type CandidateResume,
  useChangeCandidateReadinessDate,
  useUpdateCandidateResume,
} from '@entities/candidates';
import { Button, Flex, Paper, Text, Title } from '@mantine/core';
import { formatPhoneNumber, joinStringWithFallback } from '@shared/helpers';
import { getDateWithFallback, showNotification } from '@shared/utils';
import { IconPencil } from '@tabler/icons-react';
import dayjs from 'dayjs';

//
//

export type CandidateSidebarInfoProps = {
  data: CandidateResume;
};

export const CandidateSidebarInfo = ({ data }: CandidateSidebarInfoProps) => {
  const { mutate: updateCandidateResume, isPending } =
    useUpdateCandidateResume();
  const { openChangeCandidateReadinessDate } =
    useChangeCandidateReadinessDate();
  const readinessDate = getDateWithFallback(
    data.employmentDate,
    'Не установлено'
  );
  const fullName = joinStringWithFallback([data.firstName, data.lastName], {
    fallback: '-',
  });

  const handleChangeReadinessDate = (newDate: Date) => {
    updateCandidateResume(
      {
        candidateId: data.candidateId,
        resumeId: data.resumeId,
        request: {
          employmentDate: newDate.toISOString(),
        },
      },
      {
        onSuccess: () => {
          showNotification({
            variant: 'success',
            message: 'Дата готовности к выходу на работу успешно обновлена',
          });
        },
        onError: error => {
          showNotification({
            error,
            variant: 'error',
            message:
              error.response?.data.message ||
              'Не удалось обновить дату готовности к выходу на работу',
          });
        },
      }
    );
  };

  return (
    <Paper p={8} bg='primary.0' radius={16} w={{ base: '100%', md: 418 }}>
      <Flex direction='column' gap={24}>
        <Paper p={16} radius={12} bg='white'>
          <Flex direction='column' gap={20}>
            <Flex direction='column' gap={8}>
              <Text c='tertiary.7' size='lg'>
                Кандидат
              </Text>
              <Title order={4} c='primary.7'>
                {fullName}
              </Title>
            </Flex>

            <Flex direction='column' gap={8}>
              <InfoRow label='Возраст' value={data.age?.toString() || '-'} />
              <InfoRow
                label='Контактный телефон'
                value={formatPhoneNumber(data.phone) || '-'}
              />
              <InfoRow
                label='Язык общения'
                value={data.preferredCommunicationLanguage?.trim() || '-'}
              />
            </Flex>
          </Flex>
        </Paper>

        <Flex direction='column' gap={24} px={16} pb={8}>
          <Title size='lg' order={4}>
            Дата готовности к выходу на работу
          </Title>
          <Flex align='center' justify='space-between' gap={16}>
            <Text c='primary.7' size='lg'>
              {readinessDate instanceof Date
                ? dayjs(readinessDate).format('DD.MM.YYYY')
                : readinessDate}
            </Text>
            <Button
              aria-label='Изменить дату готовности'
              variant='subtle'
              size='md'
              loading={isPending}
              disabled={isPending}
              onClick={() =>
                openChangeCandidateReadinessDate({
                  initialDate:
                    readinessDate instanceof Date ? readinessDate : undefined,
                  onSave: handleChangeReadinessDate,
                })
              }
            >
              <IconPencil size={24} />
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Paper>
  );
};

//
//

type InfoRowProps = {
  label: string;
  value: string;
};

const InfoRow = ({ label, value }: InfoRowProps) => (
  <Flex justify='space-between' align='flex-start' gap={16}>
    <Text c='tertiary.7'>{label}</Text>
    <Text
      c='primary.7'
      ta='right'
      style={{ overflowWrap: 'anywhere', minWidth: 0 }}
    >
      {value}
    </Text>
  </Flex>
);
